import React, { useEffect, useState } from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import _ from 'lodash';

import CommonUtils from '../shared/utils/common.utils';
import FormValidationsUtils from '../shared/utils/formValidations.utils';

const DELIMITER = ',';
const { arrayToString, stringToArray } = CommonUtils;
const { allowedChars } = FormValidationsUtils;

export function initTargeting() {
  return { regions: [], markets: [], states: [], store_number: [] };
}

export function targetingFromAPI(t) {
  if (!t) return initTargeting();
  const obj = _.isString(t) ? JSON.parse(t) : t;
  return {
    regions: obj.region || [],
    markets: obj.market || [],
    states: obj.state || [],
    store_number: obj.store_number || [],
  };
}

export function targetingToAPI(targeting) {
  const obj = {
    region: targeting.regions || null,
    market: targeting.markets || null,
    state: targeting.states || null,
    store_number: targeting.store_number || null,
  };
  return _.omitBy(obj, _.isEmpty);
}

export default function TemplateTargetingForm({
  initialTargeting,
  onStateChanged,
  isValidating,
}) {
  const [targeting, setTargeting] = useState(
    initialTargeting || initTargeting()
  );

  const [touched, setTouched] = useState({});

  function updateTargeting(key, value) {
    setTouched((prev) => ({ ...prev, [key]: true }));
    setTargeting({ ...targeting, [key]: value });
  }

  const [validationErrors, setValidationErrors] = useState({});

  function validate() {
    const newErrors = {};

    const onlyNums = allowedChars(`0-9 ${DELIMITER}`);
    const onlyChars = allowedChars(`a-zA-Z ${DELIMITER}`);
    const { store_number, markets, states } = targeting;

    if (!onlyNums.isValid(store_number.join(DELIMITER))) {
      newErrors.store_number = onlyNums.helperText;
    }
    if (!onlyNums.isValid(markets.join(DELIMITER))) {
      newErrors.markets = onlyNums.helperText;
    }
    if (!onlyChars.isValid(states.join(DELIMITER))) {
      newErrors.states = onlyChars.helperText;
    }

    const isValid = _.isEmpty(newErrors);
    setValidationErrors(newErrors);
    return isValid;
  }

  useEffect(() => {
    onStateChanged(targeting, validate());
  }, [targeting, isValidating, onStateChanged]);

  function fieldError(key) {
    return (touched[key] || isValidating) && validationErrors[key];
  }

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#F6F9FC',
      borderRadius: 2,
      '& fieldset': { border: 'none' },
    },
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{ color: 'text.secondary', fontWeight: 600, textWrap: 'balance' }}
        >
          Targeting (optional)
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <TextField
          label="Regions"
          fullWidth
          placeholder="e.g. 44, West"
          value={arrayToString(targeting.regions, DELIMITER)}
          onChange={(e) =>
            updateTargeting('regions', stringToArray(e.target.value, DELIMITER))
          }
          error={!!fieldError('regions')}
          helperText={fieldError('regions') || 'Comma-separated'}
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          label="Market Numbers"
          fullWidth
          placeholder="e.g. 123, 456"
          value={arrayToString(targeting.markets, DELIMITER)}
          onChange={(e) =>
            updateTargeting('markets', stringToArray(e.target.value, DELIMITER))
          }
          error={!!fieldError('markets')}
          helperText={fieldError('markets') || 'Comma-separated numbers'}
          inputProps={{ style: { fontVariantNumeric: 'tabular-nums' } }}
          sx={fieldSx}
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          label="States"
          fullWidth
          placeholder="e.g. TX, CA"
          value={arrayToString(targeting.states, DELIMITER)}
          onChange={(e) =>
            updateTargeting('states', stringToArray(e.target.value, DELIMITER))
          }
          error={!!fieldError('states')}
          helperText={fieldError('states') || 'Comma-separated abbreviations'}
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          label="Store/Club Numbers"
          fullWidth
          placeholder="e.g. 5550, 1"
          value={arrayToString(targeting.store_number, DELIMITER)}
          onChange={(e) =>
            updateTargeting(
              'store_number',
              stringToArray(e.target.value, DELIMITER)
            )
          }
          error={!!fieldError('store_number')}
          helperText={fieldError('store_number') || 'Comma-separated numbers'}
          inputProps={{ style: { fontVariantNumeric: 'tabular-nums' } }}
          sx={fieldSx}
        />
      </Grid>
    </>
  );
}

TemplateTargetingForm.propTypes = {
  initialTargeting: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    states: PropTypes.arrayOf(PropTypes.string),
    store_number: PropTypes.arrayOf(PropTypes.string),
  }),
  onStateChanged: PropTypes.func.isRequired,
  isValidating: PropTypes.bool,
};

TemplateTargetingForm.defaultProps = {
  initialTargeting: null,
  isValidating: false,
};
